import { DocumentServices } from 'services/Supplier/Ged'
import { expandObj } from 'utils/functions'

import { useLoading, useModal } from '.'
import { useState } from 'react'
import { Delay } from 'services/Instances/utils'
import ProgressModal from '../components/ProgressBar/ProgressBar'
import React from 'react'


// Usado para especificar os arquivos que, caso o navegador não suporte, deverão ser baixados
const SUPPORTED_EXTENSIONS = [
  'png',
  'pdf',
  'jpg',
  'jpge',
  'jpage'
]

const DOWNLODABLE_FILE_EXTENSIONS = [
  'xlsx',
  'xls',
  'xlsm',
  'csv',
  'xlt',
  'eml',
  'msg'
]

const SERVICE_ERROR = 'csn-has-service-file-error-active'
const STOP_RETRY = 'csn-stop-file-service-retry'

interface ILocalFile {
  base64: string
  filename: string
}

export interface IViewAndDownloadProps {
  gedExternalID?: number | string
  local?: ILocalFile
}

interface IUseFileReturn {
  viewFile: (props: IViewAndDownloadProps, attempt?: number, onDownloadProgress?: (progressEvent: any) => void) => void
  downloadFile: (_props: IViewAndDownloadProps) => void
  getFileExtension: (_filename: string) => string
  convertFileToBase64: (_file: File) => unknown
}

export const convertFileToBase64 = async (file: File) => {
  return new Promise((resolver) => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(file)
    fileReader.onload = () => resolver(fileReader.result)
  })
}

const getFileType = (fileExtension: string): string => {
  const fileTypes = expandObj({
    'doc,docx':
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64',
    pdf: `application/${fileExtension};base64`,
    'png,jpg,jpeg,gif,jfif': `image/${fileExtension};base64`,
    xlsx: 'application/vnd.ms-excel;base64',
    mp3: 'audio/mpeg'
  })

  return fileTypes[fileExtension]
}

const getFileExtension = (filename: string): string =>
  filename.split('.').pop().toLowerCase()

const getByteArray = (base64: string) => {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }

  return new Uint8Array(byteNumbers)
}

const isBase64 = (base64: string): boolean =>
  /data:.*\/.*;base64,/g.test(base64)

const clearBase64 = (base64: string): string =>
  base64.replace(/data:.*\/.*;base64,/g, '')

const getFileURL = (byteArray: any, base64Type: string) => {
  const file = new Blob([byteArray], { type: base64Type })

  return URL.createObjectURL(file)
}

// __________________________________________________

const openFile = (filename: string, base64: string): void => {

  if (isBase64(base64)) {
    base64 = clearBase64(base64)
  }

  const byteArray = getByteArray(base64)
  const fileExtension = getFileExtension(filename) === 'jfif' ? 'jpeg' : getFileExtension(filename)
  const base64Type = getFileType(fileExtension)
  const fileURL = getFileURL(byteArray, base64Type)

  if (DOWNLODABLE_FILE_EXTENSIONS.includes(fileExtension)) {
    const anchor = document.createElement('a')
    anchor.href = fileURL
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(fileURL)

    return
  }

  window.open(fileURL)
}

const openFileV3 = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  
  if (DOWNLODABLE_FILE_EXTENSIONS.includes(getFileExtension(filename))) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
    return;
  }

  window.open(url);
};

const fileDownload = (filename: string, base64: string) => {
  const fileExtension = getFileExtension(filename)
  const base64Type = getFileType(fileExtension)
  const a = document.createElement('a')

  a.href = `data:${base64Type};base64,${base64}`
  a.download = `${filename || `file.${fileExtension}`}`
  a.click()
}

const fileDownloadV3 = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const getFilenameFromHeaders = (headers) => {
  const contentDisposition = headers['content-disposition'];
  if (!contentDisposition) return null;
  const matches = contentDisposition.match(/filename="?([^"]+)"?/);
  return matches ? matches[1] : null;
};

const prepareBase64 = (base64: string): string => {
  return base64.replace(/data:.*base64,/, '')
}

const isViewSupported = (filename: string): boolean => {
  return filename.match(new RegExp(`^(.*\\.(?!(${SUPPORTED_EXTENSIONS.join('|')})$))?[^.]*$`, 'gi')) === null
}

const useFile = (): IUseFileReturn => {
  const { showModal, closeModal } = useModal()
  const { setLoading } = useLoading()
  const [uploadProgress, setUploadProgress] = useState(0);

  const MAX_RETRY = 5;
  const MIN_AWAIT = 12000;

  const viewFile = async (
    { gedExternalID, local }: IViewAndDownloadProps,
    attempt=1,
    onDownloadProgress?: (progressEvent: any) => void
  ) => {
    if (window.localStorage.getItem(STOP_RETRY)) {
      window.localStorage.removeItem(STOP_RETRY);
      return;
    }
    if (
      (gedExternalID == null && (local != undefined && local.filename == undefined && local.base64 == undefined)) ||
      (gedExternalID == null && local == undefined)
    ) {
      showModal({
        hideButtonCancel: true,
        message:
          'Você está tentando acessar um documento que foi anexado antes de 28/12/2022. Documentos anteriores a esta data não estão disponíveis no portal NGT, apenas fisicamente.',
        txtButtonConfirm: 'OK',
        type: 'error',
      });
      return;
    }
    console.log(attempt,"tentativa")
    if (attempt > MAX_RETRY) {
      showModal({
        hideButtonCancel: true,
        message: 'Serviço de guarda de documento está offline!',
        txtButtonConfirm: 'OK',
        type: 'error',
      });
      return;
    }

    if (attempt <= 1) setLoading('loading');

    if (local && local.filename && local.base64) {
      if (!isViewSupported(local.filename)) {
        fileDownload(local.filename, prepareBase64(local.base64));
        setLoading('idle');
        return;
      }

      openFile(local.filename, prepareBase64(local.base64));
      setLoading('idle');
      return;
    }

    const startTime = performance.now();
    try {
      setUploadProgress(0);

      const response = await DocumentServices().SearchDocumentv3(gedExternalID as number, {
        onDownloadProgress,
        responseType:'blob' 
      });
      if (response.status === 200) {
        const blob = response.data; 
        const contentDisposition = response.headers['content-disposition'] || response.headers.get?.('content-disposition');
const filename = getDecodedFilename(contentDisposition);
        
        if (!isViewSupported(filename)) {
          fileDownloadV3(blob, filename);
          setLoading('idle');
        } else {
          openFileV3(blob, filename);
          setLoading('idle');
        }
        } else {
          setLoading('idle');
          showModal({
            hideButtonCancel: true,
            message: 'Houve um erro ao visualizar o arquivo',
            txtButtonConfirm: 'OK',
            type: 'error',
          });
        }
      } 
     catch (error: any) {
      setLoading('idle');
      const endTime = performance.now();
      const elapsedTime = endTime - startTime;

      if (window.localStorage.getItem(STOP_RETRY)) {
        window.localStorage.removeItem(STOP_RETRY);
        return;
      }

      showModal({
        hideButtonCancel: true,
        message: `Serviço de guarda de documento não está respondendo a solicitação. Aguarde enquanto realizamos uma nova tentativa: ${attempt} de ${MAX_RETRY}...`,
        handleConfirm: () => {
          window.localStorage.setItem(STOP_RETRY, 'true');
          return;
        },
        txtButtonConfirm: 'Cancelar',
        type: 'alert',
      });

      if (elapsedTime < MIN_AWAIT) await Delay(MIN_AWAIT - elapsedTime);
      viewFile({ gedExternalID, local }, attempt + 1, onDownloadProgress);
    }
  };

  const downloadFile = async ({
    gedExternalID,
    local
  }: IViewAndDownloadProps) => {
    if (local && local.filename && local.base64) {
      fileDownload(local.filename, local.base64)
      setLoading('idle')
      return
    }

    showModal({
      hideButtonCancel: true,
      message: (
        `Aguarde o download do documento irá iniciar...`
      ),
      txtButtonConfirm: null,
    });


    const response = await DocumentServices().SearchDocumentv3(gedExternalID as number, {
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
        
        showModal({
          hideButtonCancel: true,
          message: <ProgressModal percentCompleted={percentCompleted} />,
          txtButtonConfirm: 'OK',
        });
      },responseType:'blob'
    });

    if (response?.status === 200) {
      const blob = response.data; 

      const contentDisposition = response.headers['content-disposition'] || response.headers.get?.('content-disposition');
const filename = getDecodedFilename(contentDisposition);


      if (filename) {
        fileDownloadV3(blob,filename)
      } else {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Houve um erro ao fazer o download do arquivo',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      }
    } else {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: 'Houve um erro ao fazer o download do arquivo',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
    }

    setLoading('idle')
  }

  return {
    convertFileToBase64,
    downloadFile,
    getFileExtension,
    viewFile
  }
}
const getDecodedFilename = (contentDisposition) => {
  if (!contentDisposition) return 'arquivo_desconhecido';

 
  let match = contentDisposition.match(/filename\*=\s*UTF-8''([^;]+)/);
  if (match) {
    return decodeURIComponent(decodeURIComponent(match[1])); 
  }

  
  match = contentDisposition.match(/filename="?([^";]+)"?/);
  if (match) {
    return decodeURIComponent(decodeURIComponent(match[1])); 
  }

  return 'arquivo_desconhecido';
};
export default useFile
